<template>
  <!-- MBR044/MBR045 Store — member_app_UI/song_fa_members_rewards_store_mobile -->
  <div class="page-header align-items-start min-vh-screen mbr-store">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <header class="mbr-store__bar">
        <div class="mbr-store__head">
          <h1 class="mbr-store__title">{{ $t('reward.title') }}</h1>
          <!-- point_format is "8 Point", already formatted by the API; cashback
               merchants have no points balance to show -->
          <span
            v-if="merchantMember.reward_type == rewardPoint"
            class="mbr-store__points"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path stroke-linecap="round" d="M12 7v5l3 3" /></svg>
            <span>{{ merchantMember.point_format }}</span>
          </span>
        </div>
        <!-- Store is this screen; the other two segments navigate, nothing else changes there -->
        <nav class="mbr-store__segment" :aria-label="$t('reward.sections')">
          <button type="button" class="mbr-store__seg" @click="go('/my-rewards')">
            {{ $t('reward.tab_my_vouchers') }}
          </button>
          <button type="button" class="mbr-store__seg" @click="go('/reward-catalogue')">
            {{ $t('reward.tab_catalogue') }}
          </button>
          <button type="button" class="mbr-store__seg mbr-store__seg--active">
            {{ $t('reward.tab_store') }}
          </button>
        </nav>
      </header>

      <main class="mbr-store__body" v-if="isLoadDone">
        <!-- MBR106 same Wallet Balance card as the Catalogue tab -->
        <MbrWalletCard class="mbr-store__wallet" :amount="merchantMember.amount_format" />
        <div class="mbr-store__tools">
          <label class="mbr-store__search">
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path stroke-linecap="round" d="M21 21l-4.35-4.35" /></svg>
            <!-- unlike /member/rewards, this endpoint filters on search_keyword
                 server-side, so the debounced reload stays -->
            <input
              id="keyword_search"
              type="search"
              name="keyword_search"
              :placeholder="$t('store.search_placeholder')"
              :value="searchKeyword"
              @input="onChangesearchKeyword"
            />
          </label>
          <!-- Sort / Filter are built here rather than in the shared SortFilterToolbar,
               which no screen renders any more; they open the same two sheets -->
          <button
            type="button"
            class="mbr-store__tool"
            :aria-label="$t('sort.title')"
            data-bs-toggle="modal"
            data-bs-target="#bottom_modal_sort"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M6 12h12m-9 6h6" /></svg>
          </button>
          <button
            type="button"
            class="mbr-store__tool"
            :class="{ 'mbr-store__tool--on': hasFilter }"
            :aria-label="$t('filter.title')"
            data-bs-toggle="modal"
            data-bs-target="#bottom_modal_filter"
          >
            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4 21v-7m0-4V3m8 18v-9m0-4V3m8 18v-5m0-4V3M1 14h6m2-6h6m2 8h6" /></svg>
            <span v-if="hasFilter" class="mbr-store__dot" aria-hidden="true"></span>
          </button>
        </div>

        <div class="mbr-store__section">
          <span class="mbr-store__section-label">
            {{ $t('store.available_bundles', { count: merchantVouchers.length }) }}
          </span>
        </div>

        <div v-if="merchantVouchers.length > 0" class="mbr-store__list">
          <article
            v-for="(value, index) in merchantVouchers"
            :key="index"
            class="mbr-store__card"
          >
            <div
              class="mbr-store__banner"
              :class="{ 'mbr-store__banner--blank': !hasImage(value) }"
              @click="bundleDetail(value.id)"
            >
              <template v-if="hasImage(value)">
                <img :src="value.icon_url" :alt="value.reward_title" />
                <span class="mbr-store__scrim" aria-hidden="true"></span>
              </template>
              <span v-else class="mbr-store__medallion" aria-hidden="true">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path stroke-linecap="round" d="M12 6v6l4 2" /></svg>
              </span>
              <span
                class="mbr-store__chip"
                :class="{ 'mbr-store__chip--none': value.number_of_vouchers < 1 }"
              >
                <svg v-if="value.number_of_vouchers > 0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                {{ voucherCountText(value) }}
              </span>
              <h3 class="mbr-store__name">{{ value.reward_title }}</h3>
            </div>
            <div class="mbr-store__foot">
              <div class="mbr-store__price-wrap">
                <div class="mbr-store__price">
                  <span>{{ value.amount_text }}</span>
                  <!-- bundles can cost money, points, or both — the design's third
                       card is the hybrid case -->
                  <small v-if="Number(value.points_convert) > 0">
                    + {{ value.point_text }}
                  </small>
                </div>
                <p v-if="value.expiry_date_text" class="mbr-store__valid">
                  {{ validityText(value.expiry_date_text, 'reward') }}
                </p>
              </div>
              <!-- same condition VoucherCard used: a bundle payable by card is
                   still buyable when the balance path is disabled -->
              <button
                v-if="!value.is_disabled || !value.is_disabled_by_card"
                type="button"
                class="mbr-store__buy"
                data-bs-toggle="modal"
                data-bs-target="#bottom_modal"
                @click="exchange(value)"
              >
                {{ $t('store.buy') }}
              </button>
              <button v-else type="button" class="mbr-store__buy mbr-store__buy--off" disabled>
                {{ $t('reward.unavailable') }}
              </button>
            </div>
          </article>
        </div>
        <div v-else class="mbr-store__empty">
          {{ searchKeyword.trim() ? $t('store.no_match') : $t('store.no_voucher') }}
        </div>
      </main>

      <div class="position-fixed top-1 end-1 z-index-2">
        <material-snackbar
          v-if="snackbar === 'danger' || snackbar === 'success'"
          title
          date
          :description="message"
          :icon="{ component: 'campaign', color: 'white' }"
          :color="snackbar"
          :close-handler="closeSnackbar"
        />
      </div>
    </div>
    <!-- Modal -->
    <Sort :sortType="sortType" :filterData="filterData" :changeSortType="changeSortType" />
    <Filter
      :filterRewardType="filterRewardType"
      :filterArrRewardType="filterArrRewardType"
      :stores="stores"
      :filterStore="filterStore"
      :filterArrStore="filterArrStore"
      :filterData="filterData"
      :resetData="resetData"
    />
    <Exchange
      :rewardPerTransaction="merchantMember.reward_type ?? 0"
      :voucherData="previewVoucherExchange"
      :amount_format="merchantMember.amount_format"
      :amount_in_account="merchantMember.amount_in_account"
      :point_format="merchantMember.point_format"
      :point_in_account="merchantMember.point"
      :is_disabled="previewVoucherExchange.is_disabled"
      :is_disabled_by_card="previewVoucherExchange.is_disabled_by_card"
      :confirm-exchange="confirmExchange"
      :keyReload="keyReloadExchange"
    />
    <material-loading :active="loaderActive" />
    <ModalConfirm
      :message="$t('reward.confirm_exchange')"
      :btnYes="$t('common.yes')"
      :sycn-profile="processExchange"
    />
    <ModalSuccess
      :processAcction="gotoMyReward"
      :message="$t('reward.exchange_success')"
      :buttonText="$t('reward.go_to_my_reward')"
    />
    <QrcodeMember />
    <MbrBottomNav />
  </div>
</template>
<script>
import { mapMutations, mapActions, mapState } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import RewardService from "../services/reward.service";
import StripeService from "../services/stripe.service";
import AirwallexService from "../services/airwallex.service";
import "bootstrap/dist/js/bootstrap.bundle";
import Exchange from "@/components/Exchange.vue";
import Sort from "@/components/Sort.vue";
import Filter from "@/components/Filter.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import ModalConfirm from "@/components/ModalConfirm.vue";
import QrcodeMember from "@/components/QrcodeMember.vue";
import MbrBottomNav from "@/components/MbrBottomNav.vue";
import MbrWalletCard from "@/components/MbrWalletCard.vue";
import "bootstrap/js/dist/modal";
import $ from "jquery";
import { getMerchantCode } from "@/config/utils";
import { voucherCountText, validityText } from "@/i18n/apiText";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "my-transacions",
  components: {
    MaterialSnackbar,
    Exchange,
    MaterialLoading,
    ModalSuccess,
    Sort,
    Filter,
    ModalConfirm,
    QrcodeMember,
    MbrBottomNav,
    MbrWalletCard,
  },
  data() {
    return {
      listTransaction: [],
      membershipCashback: 0,
      transactionType: [],
      merchantVouchers: [],
      currentMember: {},
      merchantMember: {},
      previewVoucherExchange: {},
      snackbar: null,
      loaderActive: false,
      merchantCode: "",
      stores: [],
      filterArrRewardType: [],
      filterArrStore: [],
      params: {},
      sortType: "1",
      isLoadDone: false,
      pointIcon: "",
      keyReloadExchange: "keyReloadExchange",
      searchKeyword: "",
      searchTimerId: null,
      // reward_per_transaction: 1 cashback, 2 point
      rewardPoint: 2,
      // asset() fallback the API returns when a bundle has no icon
      noImageUrl: "media/noimage.png",
    };
  },
  async created() {
    const code = getMerchantCode();
    var session_id = this.$route.query.session_id;
    var airwallex_intent_id = this.$route.query.airwallex_intent_id;

    if (session_id) {
      // call back after pay success - stripe
      this.showLoader();
      await StripeService.checkCheckoutSessionStripe(
        { stripe_id: session_id },
        code,
      ).then(
        (response) => {
          if (response.data.result.isSuccess) {
            $(".btn-success-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.setTimeOutNotification();
          this.hideLoader();
        },
      );
    } else if (airwallex_intent_id) {
      // Airwallex HPP return — verify with check-hpp-airwallex. BE validates
      // metadata + dispatches ProcessWebhookJob (handleExchangeVoucherBundle)
      // which actually issues the voucher bundle. Idempotent with webhook.
      // Centralised here so any parent that opened the Exchange modal can use
      // a single redirect URL (/{merchant_code}/store).
      this.showLoader();
      await AirwallexService.checkHppAirwallex(
        { payment_intent_id: airwallex_intent_id },
        code,
      ).then(
        (response) => {
          // Checked once; drop the id so a reload doesn't show the result again.
          const query = { ...this.$route.query };
          delete query.airwallex_intent_id;
          this.$router.replace({ query });

          if (response.data.result.isSuccess) {
            $(".btn-success-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error, 'store.payment_check_failed');
          this.setTimeOutNotification();
          this.hideLoader();
        },
      );
    }

    // MBR096 — sort/filter/search live in the URL query, so the bundle detail's
    // back ($router.go(-1)) lands on them again instead of a fresh, unfiltered store
    this.restoreQuery();

    await this.getDataHomePage();
    await this.getBranches();

    this.isLoadDone = true;
  },

  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer so MbrBottomNav's ACCOUNT can slide it in
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },

  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },

  computed: {
    hasFilter() {
      return this.filterArrRewardType.length > 0 || this.filterArrStore.length > 0;
    },
  },

  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
  },
  methods: {
    validityText,
    voucherCountText,
    ...mapMutations([
      "toggleEveryDisplay",
      "toggleHideConfig",
      "toggleNavbarOnly",
      "navbarMinimize",
    ]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
    ...mapState(["isPinned"]),

    go(path) {
      this.$router.push(this.merchantCode + path);
    },

    gotoMyReward() {
      $(".btn-close").click();
      this.$router.push(this.merchantCode + "/my-rewards");
    },

    // icon_url is never empty: bundles without an icon get media/noimage.png,
    // which the design replaces with the medallion tile
    hasImage(voucher) {
      const url = voucher.icon_url ?? "";
      return url !== "" && !url.includes(this.noImageUrl);
    },

    toggleSidebar() {
      this.navbarMinimize();
    },

    exchange(voucher) {
      this.keyReloadExchange = Math.random();
      this.previewVoucherExchange = voucher;
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    async getDataHomePage() {
      this.closeSnackbar();
      await this.getVoucherBundle(this.currentParams());
    },

    // every reload sends the whole state, so a search keeps the applied sort/filter
    currentParams() {
      const params = { ...this.params };
      if (this.searchKeyword.trim()) {
        params.search_keyword = this.searchKeyword;
      }
      return params;
    },

    restoreQuery() {
      const query = this.$route.query;
      const toIds = (value) =>
        String(value ?? "")
          .split(",")
          .filter(Boolean)
          .map(Number)
          .filter((id) => !Number.isNaN(id));
      if (query.sort) {
        this.sortType = String(query.sort);
      }
      this.filterArrRewardType = toIds(query.type);
      this.filterArrStore = toIds(query.store);
      this.searchKeyword = String(query.q ?? "");
      // a plain /store must still send {} — listByMember skips ORDER BY entirely
      // then, while sort_type 1 would switch the first load to newest-first
      if (!query.sort && !query.type && !query.store) {
        return;
      }
      this.params = {
        sort_type: this.sortType,
        filter_reward_type: [...this.filterArrRewardType],
        filter_store: [...this.filterArrStore],
      };
    },

    // replace, not push: the query only mirrors state, it is not a history step.
    // Reads the applied params, not chips toggled in the sheet but never applied
    syncQuery() {
      const applied = this.params;
      const query = {};
      if (applied.sort_type && applied.sort_type !== "1") query.sort = applied.sort_type;
      if (applied.filter_reward_type?.length) query.type = applied.filter_reward_type.join(",");
      if (applied.filter_store?.length) query.store = applied.filter_store.join(",");
      if (this.searchKeyword.trim()) query.q = this.searchKeyword;
      this.$router.replace({ path: this.$route.path, query });
    },

    bundleDetail(rewardId) {
      this.$router.push(this.merchantCode + "/voucher-bundle/" + rewardId);
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.syncQuery();
        this.getVoucherBundle(this.currentParams());
      }, 1000);
    },

    async getVoucherBundle(params) {
      this.showLoader();
      await RewardService.getListVoucherBundle(params).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.merchantVouchers = response.data.data.vouchers;
            this.currentMember = response.data.data.member;
            this.merchantMember = response.data.data.merchant_member;
            this.pointIcon = response.data.data.point_icon;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        },
      );
    },

    confirmExchange() {
      $(".btn-confirm-modal").click();
    },

    processExchange() {
      $(".btn-close-confirm-modal").click();
      this.closeSnackbar();
      this.showLoader();
      const rewardId = this.previewVoucherExchange.id ?? 0;
      RewardService.processExchangeVoucherBundle(rewardId).then(
        (response) => {
          if (response.data.result.isSuccess) {
            $(".btn-close").click();
            this.getVoucherBundle(this.currentParams());
            $(".btn-close").click();
            $(".btn-success-modal").click();
          } else {
            $(".btn-close").click();
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        },
      );
    },

    changeSortType(event) {
      this.sortType = event.target.value;
    },

    filterRewardType(value) {
      const index = this.filterArrRewardType.indexOf(value);
      if (index > -1) {
        this.filterArrRewardType.splice(index, 1);
      } else {
        this.filterArrRewardType.push(value);
      }
    },

    filterStore(value) {
      const index = this.filterArrStore.indexOf(value);
      if (index > -1) {
        this.filterArrStore.splice(index, 1);
      } else {
        this.filterArrStore.push(value);
      }
    },

    async getBranches() {
      this.stores = [];
      this.showLoader();
      await RewardService.getBranches().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.stores = response.data.data;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        },
      );
    },

    filterData() {
      $(".btn-close").click();
      $(".x-close").click();
      this.params = {
        sort_type: this.sortType,
        filter_reward_type: [...this.filterArrRewardType],
        filter_store: [...this.filterArrStore],
      };
      this.syncQuery();
      this.getVoucherBundle(this.currentParams());
    },
    resetData() {
      $(".btn-unclick").css({ "background-color": "#E3EBF7", color: "#000" });
      $(".btn-unclick2").css({ "background-color": "#E3EBF7", color: "#000" });
      this.filterArrRewardType = [];
      this.filterArrStore = [];
    },

    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
  },
};
</script>
<style scoped>
/* MBR044/MBR045 Store — member_app_UI/song_fa_members_rewards_store_mobile.
   The legacy unscoped block that used to live here (`* { font-family: Poppins }`,
   the ticket `.card`, `.col-lg-8`, `.font-level-member`) moved verbatim into
   assets/css/custom.css, because Admissions/MyTransactions and the remaining
   Material Dashboard leftovers still read those rules. */

/* Material Dashboard's `.page-header { overflow: hidden }` would stop the header sticking */
.mbr-store.page-header {
  overflow: visible;
  overflow-x: clip;
  /* room for the fixed MbrBottomNav */
  padding-bottom: 120px;
  font-family: var(--mbr-font-body);
}

/* custom.css still ships an app-wide `* { font-family: Poppins }`; :where() keeps
   this at one attribute's specificity so the class rules below still win */
.mbr-store :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-store .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

/* mobile column, like Home, Account, My Vouchers and the Catalogue */
.mbr-store .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-store__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 24px 20px 12px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-store__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-store__title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 24px;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-strong);
}

.mbr-store__points {
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: var(--mbr-color-tint);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-full);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--mbr-color-primary);
}

.mbr-store__points svg {
  flex: none;
  width: 14px;
  height: 14px;
}

.mbr-store__segment {
  display: flex;
  margin-top: 16px;
  padding: 4px;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-store__seg {
  flex: 1 1 0;
  padding: 8px 4px;
  background: none;
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
  transition: background 0.15s ease, color 0.15s ease;
}

.mbr-store__seg--active {
  background: var(--mbr-color-primary);
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-store__body {
  padding: 16px 20px 0;
}

.mbr-store__wallet {
  margin-bottom: 16px;
}

/* search + sort/filter row */
.mbr-store__tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mbr-store__search {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 8px 14px;
  background: var(--mbr-color-tint-dark);
  border: 1px solid transparent;
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-ink-secondary);
  transition: border-color 0.15s ease;
}

.mbr-store__search:focus-within {
  border-color: var(--mbr-color-primary);
}

.mbr-store__search svg {
  flex: none;
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

/* outranks the global `input.nosubmit` / `input[type=search]` rules in custom.css */
.mbr-store__search input[type="search"] {
  width: 100%;
  min-width: 0;
  padding: 0;
  background: none;
  border: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-store__search input[type="search"]:focus {
  border: 0 !important;
  outline: none;
}

.mbr-store__search input[type="search"]::placeholder {
  color: var(--mbr-color-ink-muted);
  text-align: left;
  opacity: 1;
}

.mbr-store__tool {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  transition: transform 0.12s ease;
}

.mbr-store__tool:active {
  transform: scale(0.95);
}

.mbr-store__tool svg {
  width: 16px;
  height: 16px;
}

.mbr-store__tool--on {
  border-color: var(--mbr-color-primary);
}

.mbr-store__dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  background: var(--mbr-color-primary);
  border: 1.5px solid var(--mbr-color-surface);
  border-radius: var(--mbr-radius-full);
}

.mbr-store__section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
}

.mbr-store__section-label {
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.08em;
  color: var(--mbr-color-ink-secondary);
  text-transform: uppercase;
}

/* bundle cards */
.mbr-store__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
}

.mbr-store__card {
  overflow: hidden;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: var(--mbr-radius-xl);
  box-shadow: var(--mbr-shadow-card);
}

.mbr-store__banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 176px;
  overflow: hidden;
  background: #2c1010;
  cursor: pointer;
}

.mbr-store__banner--blank {
  background: var(--mbr-color-tint-dark);
}

.mbr-store__banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.88;
}

.mbr-store__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(var(--mbr-color-ink-headline-rgb), 0.9),
    rgba(var(--mbr-color-ink-headline-rgb), 0) 50%,
    rgba(var(--mbr-color-shadow-rgb), 0.3)
  );
}

.mbr-store__medallion {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
  box-shadow: inset 0 2px 4px rgba(var(--mbr-color-primary-rgb), 0.08);
}

.mbr-store__medallion svg {
  width: 28px;
  height: 28px;
}

.mbr-store__chip {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-store__chip--none {
  background: var(--mbr-color-palepink);
}

.mbr-store__chip svg {
  flex: none;
  width: 12px;
  height: 12px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-store__name {
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;
  margin: 0;
  overflow: hidden;
  font-family: var(--mbr-font-headline);
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: normal;
  color: var(--mbr-color-on-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.mbr-store__banner--blank .mbr-store__name {
  font-size: 16px;
  color: var(--mbr-color-ink-headline);
}

.mbr-store__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.mbr-store__price-wrap {
  min-width: 0;
}

.mbr-store__price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  font-family: var(--mbr-font-headline);
  font-size: 20px;
  font-weight: 800;
  line-height: 26px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-strong);
}

.mbr-store__price small {
  font-size: 14px;
  font-weight: 700;
  color: var(--mbr-color-primary);
}

.mbr-store__valid {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.7);
}

.mbr-store__buy {
  flex: none;
  padding: 10px 24px;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  line-height: 18px;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
  transition: transform 0.12s ease;
}

.mbr-store__buy:active {
  transform: scale(0.97);
}

.mbr-store__buy--off {
  background: var(--mbr-color-tint-dark);
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.6);
  box-shadow: none;
  cursor: not-allowed;
}

.mbr-store__empty {
  margin-top: 12px;
  padding: 40px 20px;
  background: var(--mbr-color-tint);
  border: 1px dashed var(--mbr-color-outline);
  border-radius: var(--mbr-radius-lg);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--mbr-color-ink-secondary);
  text-align: center;
}
</style>
